/**
 * Funções de Formatação
 *
 * Funções utilitárias para formatação de dados,
 * como moeda, datas, telefones, documentos, etc.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

/**
 * Formata valor como moeda brasileira (BRL)
 *
 * @param value - Valor numérico a ser formatado
 * @returns String formatada como moeda (ex: "R$ 1.234,56")
 *
 * @example
 * formatCurrency(1234.56) // "R$ 1.234,56"
 * formatCurrency(0) // "R$ 0,00"
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Formata valor como número com casas decimais
 *
 * @param value - Valor numérico
 * @param decimals - Número de casas decimais (padrão: 2)
 * @returns String formatada
 */
export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Formata valor como porcentagem
 *
 * @param value - Valor numérico (ex: 0.15 para 15%)
 * @param decimals - Casas decimais
 * @returns String formatada como porcentagem
 */
export function formatPercent(value: number, decimals = 0): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Formata data no padrão brasileiro
 *
 * @param date - Data a ser formatada (string ISO ou Date)
 * @returns String formatada (ex: "24/01/2024")
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR').format(d);
}

/**
 * Formata data e hora
 *
 * @param date - Data a ser formatada
 * @returns String formatada (ex: "24/01/2024 às 14:30")
 */
export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d);
}

/**
 * Formata apenas hora
 *
 * @param date - Data a ser formatada
 * @returns String formatada (ex: "14:30")
 */
export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Formata data relativa (ex: "há 5 minutos")
 *
 * @param date - Data a ser formatada
 * @returns String com tempo relativo
 */
export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'agora mesmo';
  }

  if (diffMins < 60) {
    return `há ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
  }

  if (diffHours < 24) {
    return `há ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
  }

  if (diffDays < 7) {
    return `há ${diffDays} ${diffDays === 1 ? 'dia' : 'dias'}`;
  }

  // Mais de 7 dias, mostra a data
  return formatDate(d);
}

/**
 * Formata telefone brasileiro
 *
 * @param phone - Telefone (apenas números)
 * @returns Telefone formatado
 *
 * @example
 * formatPhone("11999998888") // "(11) 99999-8888"
 * formatPhone("1133334444") // "(11) 3333-4444"
 */
export function formatPhone(phone: string): string {
  // Remove tudo que não é número e o DDI do Brasil (+55), se houver
  let numbers = phone.replace(/\D/g, '');
  if ((numbers.length === 12 || numbers.length === 13) && numbers.startsWith('55')) numbers = numbers.slice(2);

  // Celular (11 dígitos)
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }

  // Fixo (10 dígitos)
  if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }

  // Retorna o original se não bater
  return phone;
}

/**
 * Formata CPF
 *
 * @param cpf - CPF (apenas números)
 * @returns CPF formatado (ex: "123.456.789-00")
 */
export function formatCPF(cpf: string): string {
  const numbers = cpf.replace(/\D/g, '');

  if (numbers.length !== 11) {
    return cpf;
  }

  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Formata CNPJ
 *
 * @param cnpj - CNPJ (apenas números)
 * @returns CNPJ formatado (ex: "12.345.678/0001-90")
 */
export function formatCNPJ(cnpj: string): string {
  const numbers = cnpj.replace(/\D/g, '');

  if (numbers.length !== 14) {
    return cnpj;
  }

  return numbers.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    '$1.$2.$3/$4-$5',
  );
}

/**
 * Formata documento (CPF ou CNPJ)
 *
 * @param document - Documento (apenas números)
 * @returns Documento formatado
 */
export function formatDocument(document: string): string {
  const numbers = document.replace(/\D/g, '');

  if (numbers.length === 11) {
    return formatCPF(numbers);
  }

  if (numbers.length === 14) {
    return formatCNPJ(numbers);
  }

  return document;
}

/**
 * Formata CEP
 *
 * @param cep - CEP (apenas números)
 * @returns CEP formatado (ex: "01234-567")
 */
export function formatCEP(cep: string): string {
  const numbers = cep.replace(/\D/g, '');

  if (numbers.length !== 8) {
    return cep;
  }

  return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Formata tamanho de arquivo
 *
 * @param bytes - Tamanho em bytes
 * @returns String formatada (ex: "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Formata número do pedido com zeros à esquerda
 *
 * @param number - Número do pedido
 * @param digits - Quantidade de dígitos (padrão: 4)
 * @returns Número formatado (ex: "#0042")
 */
export function formatOrderNumber(number: number, digits = 4): string {
  return `#${String(number).padStart(digits, '0')}`;
}

/**
 * Trunca texto adicionando reticências
 *
 * @param text - Texto a ser truncado
 * @param maxLength - Tamanho máximo
 * @returns Texto truncado
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }

  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Formata nome para exibição (primeiro e último nome)
 *
 * @param fullName - Nome completo
 * @returns Nome resumido
 *
 * @example
 * formatDisplayName("João da Silva Santos") // "João Santos"
 */
export function formatDisplayName(fullName: string): string {
  const names = fullName.trim().split(' ');

  if (names.length === 1) {
    return names[0];
  }

  return `${names[0]} ${names[names.length - 1]}`;
}

/**
 * Obtém iniciais do nome
 *
 * @param name - Nome completo
 * @param maxChars - Máximo de caracteres (padrão: 2)
 * @returns Iniciais maiúsculas
 *
 * @example
 * getInitials("João Silva") // "JS"
 * getInitials("Maria") // "MA"
 */
export function getInitials(name: string, maxChars = 2): string {
  const words = name.trim().split(' ').filter(Boolean);

  if (words.length === 0) {
    return '';
  }

  if (words.length === 1) {
    return words[0].substring(0, maxChars).toUpperCase();
  }

  return words
    .slice(0, maxChars)
    .map(word => word[0])
    .join('')
    .toUpperCase();
}

